import {
  Backdrop,
  ButtonTertiary,
  cssColorValue,
  IconMenu,
  IconX,
  screenMdMin,
  screenXsMin,
  semanticColors,
  spacer16,
  spacer4,
  spacer40,
} from '@spotify-internal/encore-web';
import React, { useContext, useRef } from 'react';
import styled from 'styled-components';

import { HeaderSchema, LanguageSelectionSchema } from 'types/navigation';
import { MarketContext } from 'providers/market';
import { useVerticalConfig } from 'providers/vertical';
import { useTranslation } from 'providers/translations/TranslationProvider';

import { verticalMasthead } from '../constants';

import { NavBarSideMenu } from './SideMenu';
import { useBackdropMenu } from './hooks/useBackdropMenu';

const MenuButtonWrapper = styled.div`
  display: none;
  @media (max-width: ${screenMdMin}) {
    display: flex;
    position: relative;
  }
`;

const OpenMenuButton = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textBase)};
  padding-inline-end: 0;

  &:hover {
    color: ${cssColorValue(semanticColors.textBase)};
    cursor: pointer;
  }

  &::after {
    display: none;
  }
`;

const CloseMenuButton = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textBase)};
  padding-inline-end: 0;
  position: absolute;
  inset-inline-end: ${spacer40};
  top: ${spacer4};
  z-index: 1021;

  &:hover {
    color: ${cssColorValue(semanticColors.textBase)};
    cursor: pointer;
  }

  &::after {
    display: none;
  }

  @media (max-width: ${screenXsMin}) {
    inset-inline-end: ${spacer16};
  }
`;
type Props = {
  headerEntry: HeaderSchema;
  languageSelectionEntry: LanguageSelectionSchema;
};

export const NavBarSideMenuWrapper = ({ headerEntry, languageSelectionEntry }: Props) => {
  const backdropRef = useRef(null);
  const { t } = useTranslation();
  const { theme, verticalName } = useVerticalConfig();
  const mastheadConfig = verticalMasthead[verticalName];
  const { dir } = useContext(MarketContext);
  const { handleOnKeyDown, closeBackdrop, openBackdrop, backdropOpen, closeBtnRef, openBtnRef } = useBackdropMenu();

  return (
    <>
      <MenuButtonWrapper>
        <OpenMenuButton
          size="medium"
          data-testid="navigation-sidemenu-trigger"
          iconOnly={IconMenu}
          tabIndex={0}
          onKeyDown={handleOnKeyDown}
          onClick={openBackdrop}
          aria-label={t(headerEntry.dropdownMenuLabelOpen)}
          aria-expanded={backdropOpen}
          ref={openBtnRef}
        />
      </MenuButtonWrapper>
      {backdropOpen && (
        <Backdrop onClose={closeBackdrop} ref={backdropRef} withPortal dir={dir}>
          <div className={theme}>
            <div className={mastheadConfig?.colorSet}>
              <CloseMenuButton
                size="medium"
                iconOnly={IconX}
                onClick={closeBackdrop}
                onKeyDown={handleOnKeyDown}
                ref={closeBtnRef}
                tabIndex={0}
                aria-label={t(headerEntry.dropdownMenuLabelClose)}
              />
              <NavBarSideMenu
                headerEntry={headerEntry}
                languageSelectionEntry={languageSelectionEntry}
                closeBackdrop={closeBackdrop}
                dir={dir}
              />
            </div>
          </div>
        </Backdrop>
      )}
    </>
  );
};
